import { apiSlice } from './apiSlice'
import type { User } from '../slices/authSlice'

export interface UserCreate {
  username: string
  email: string
  full_name?: string
  role: string
  password: string
}

export interface UserUpdate {
  email?: string
  full_name?: string
  role?: string
  password?: string
}

export const usersApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<User[], { role?: string } | void>({
      query: (params) => {
        const queryParams = params?.role ? `?role=${params.role}` : ''
        return `/users/list${queryParams}`
      },
      providesTags: ['User'],
    }),
    getUser: builder.query<User, string>({
      query: (id) => `/users/${id}`,
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),
    createUser: builder.mutation<User, UserCreate>({
      query: (body) => ({
        url: '/users/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    updateUser: builder.mutation<User, { id: string } & UserUpdate>({
      query: ({ id, ...data }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }],
    }),
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    getUserByEmail: builder.query<User, string>({
      query: (email) => `/users/by-email/${encodeURIComponent(email)}`,
      providesTags: (result, error, email) => [{ type: 'User', id: result?.id }],
    }),
    getUsersBatch: builder.query<User[], string[]>({
      query: (userIds) => ({
        url: '/users/batch',
        method: 'POST',
        body: userIds,
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'User' as const, id })), 'User']
          : ['User'],
    }),
    fetchUsersBatch: builder.mutation<User[], string[]>({
      query: (userIds) => ({
        url: '/users/batch',
        method: 'POST',
        body: userIds,
      }),
    }),
  }),
})

export const {
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUserByEmailQuery,
  useLazyGetUserByEmailQuery,
  useGetUsersBatchQuery,
  useFetchUsersBatchMutation,
} = usersApi
